// /* eslint-disable class-methods-use-this */
import { BanksDto } from '../dtos/banks-dto';
import QuotationDto, {
  CashVehicleQuoteApiRequest,
  CashVehicleQuoteApiResponse,
  CashVehicleQuoteRequest,
  CashVehicleQuoteResponse,
  QuoteDto,
  QuoteOfferDto,
  RecommendedPlansApiRequest,
  RecommendedPlansApiResponse,
  RecommendedPlansRequest,
  RecommendedPlansResponse,
  RoutingPlansApiRequest,
  RoutingPlansApiResponse,
  RoutingPlansRequest,
  RoutingPlansResponse,
} from '../dtos/quotation-dto';
import QuotationEssentialResponseDto, {
  QuoteEssentialDto,
} from '../dtos/quotation-essential-dto';
import { QuotationPlanDto } from '../dtos/quotation-plan-dto';
import { RemarketingDto } from '../dtos/remarketing-dto';
import { ZoneDto } from '../dtos/zone-dto';
import Banks from '../models/bank';
import { Quotation, Quote, QuoteOffer } from '../models/quotation';
import {
  QuotationEssential,
  QuotationEssentialResponse,
} from '../models/quotation-essential';
import QuotationPlan from '../models/quotation-plan';
import { Remarketing } from '../models/remarketing';
import { Zone } from '../models/zone';

export default class QuotationMapping {
  formatCreateQuote(data: Quote): QuoteDto {
    return {
      identificadorCliente: data.clientId,
      codigoPromocion: data.promotionalCode,
      codigoOferta: data.offerCode,
      identificadorSolicitud: data.requestId,
      identificadorVehiculo: data.vehicleId,
      tipoCotizacion: data.typeOfQuote,
    };
  }
  formatCreateQuoteResponse(data: QuotationDto): Quotation {
    return {
      jobNumber: data.numeroTrabajo,
      offerCode: data.codigoOferta,
      totalPremium: data.primaTotal,
      netPremium: data.primaNeta,
      variablePremium: data.primaVariable,
      emissionFee: data.tasaEmision,
      commissionPercentage: data.comisionPorcentaje,
      igv: data.igv,
      requiresGps: data.requiereGps,
      requiresInspection: data.requiereInspeccion,
      currencyValidation: data.validacionMoneda,
      periodId: data.identificadorPeriodo,
      annualFixedPremiumGps: data.primaAnualFijaGps,
      installments: data.pagoPlazos,
      currentAmount: data.importeActual,
    };
  }

  getQuotationPlans(data: QuotationPlanDto[]): QuotationPlan[] {
    return data.map((item: QuotationPlanDto) => {
      return {
        planId: item.identificadorPlan,
        name: item.nombre,
        numberOfPayments: item.numeroPagos,
        interestRate: item.tipoInteres,
      };
    });
  }

  getBanks(data: BanksDto[]): Banks[] {
    return data.map((item: BanksDto) => {
      return {
        ruc: item.ruc,
        name: item.nombre,
      };
    });
  }

  getZones(data: ZoneDto[]): Zone[] {
    return data.map((item: ZoneDto) => {
      return {
        code: item.codigo,
        name: item.nombre,
        region: item.region,
      };
    });
  }

  getQuoteAllOffers(data: QuoteOfferDto): QuoteOffer {
    return {
      code: data.codigo,
      alias: data.alias,
      name: data.nombre,
      fullName: data.nombreCompleto,
      orderNum: data.numeroOrden,
    };
  }

  toCashVehicleQuoteApiRequest = (
    payload: CashVehicleQuoteRequest,
  ): CashVehicleQuoteApiRequest => ({
    identificadorCliente: payload.customerId,
    identificadorVehiculo: payload.vehicleId,
    zonaCirculacion: payload.circulationZone,
    oferta: payload.offer,
  });

  toCashVehicleQuoteResponse = (
    payload: CashVehicleQuoteApiResponse,
  ): CashVehicleQuoteResponse => ({
    offerCode: payload.codigoOferta,
    planId: payload.identificadorPlan,
    verified: payload.verificado,
    paymentInstallment: {
      amount: payload.pagoPlazos.monto,
      name: payload.pagoPlazos.nombre,
      planId: payload.pagoPlazos.identificadorPlan,
    },
  });

  formatGetRecommendedPlansRequest = (
    data: RecommendedPlansRequest,
  ): RecommendedPlansApiRequest => ({
    cliente: {
      numeroDocumento: data.documentId,
      tipoDocumento: data.documentType,
    },
    vehiculo: {
      numeroPlaca: data.plate,
    },
  });

  formatGetRoutingPlansRequest = (
    data: RoutingPlansRequest,
  ): RoutingPlansApiRequest => ({
    cliente: {
      ubigeo: data.ubigeo,
    },
    vehiculo: {
      anio: Number(data.year),
      sumaAsegurada: Number(data.sumInsured),
      tipoRiesgo: data.typeRisk,
    },
  });

  formatGetRecommendedPlansResponse = (
    data: RecommendedPlansApiResponse,
  ): RecommendedPlansResponse => ({
    recommendedPlans: data.planesRecomendados,
  });

  formatGetRoutingPlansResponse = (
    data: RoutingPlansApiResponse,
  ): RoutingPlansResponse => ({
    plans: {
      planOne: data.planes.plan1,
      planTwo: data.planes.plan2,
      planThree: data.planes.plan3,
      planFour: data.planes.plan4,
    },
  });

  getRemarketingResponse(data: RemarketingDto): Remarketing {
    return {
      customer: {
        id: data.cliente?.identificadorCliente,
        email: data.cliente?.correo,
        documentId: data.cliente?.numeroDocumento,
        documentType: data.cliente?.tipoDocumento,
        names: data.cliente?.nombres,
        paternalSurname: data.cliente?.apellidoPaterno,
        maternalSurname: data.cliente?.apellidoMaterno,
        phoneNumber: data.cliente?.numeroTelefono,
      },
      plan: {
        id: data.planAutoEfectivo?.identificadorPlan,
        monthlyAmount: data.planAutoEfectivo?.montoMensual,
        totalAmount: data.planAutoEfectivo?.montoAnual,
        theftAmount: data.planAutoEfectivo?.montoRobo,
        offerCode: data.planAutoEfectivo?.codigoOferta,
      },
      vehicle: {
        id: data.vehiculo?.identificadorVehiculo,
        plate: data.vehiculo?.placa,
        vehicleType: data.vehiculo?.tipo,
        brand: data.vehiculo?.marca,
        model: data.vehiculo?.modelo,
        year: data.vehiculo?.anio,
        vehicleClass: data.vehiculo?.clase,
        useType: data.vehiculo?.tipoUso,
        riskType: data.vehiculo?.tipoRiesgo,
        numberSeats: data.vehiculo?.asientos,
      },
    };
  }

  formatEssentialQuote(data: QuotationEssential): QuoteEssentialDto {
    return {
      zonaCirculacion: data.circulationAreaCode,
      canalPrimario: data.primaryChannel,
      canalSecundario: data.secondaryChannel,
      identificadorVehiculo: data.vehicleId,
      identificadorCliente: data.clientId,
    };
  }

  formatCreateQuoteEssentialResponse(
    data: QuotationEssentialResponseDto,
  ): QuotationEssentialResponse {
    return {
      jobNumber: data.numeroTrabajo,
      offerCode: data.codigoOferta,
      totalPremium: data.primaTotal,
      netPremium: data.primaNeta,
      variablePremium: data.primaVariable,
      emissionFee: data.tasaEmision,
      commissionPercentage: data.comisionPorcentaje,
      igv: data.igv,
      requiresGps: data.requiereGps,
      requiresInspection: data.requiereInspeccion,
      currencyValidation: data.validacionMoneda,
      periodId: data.identificadorPeriodo,
      annualFixedPremiumGps: data.primaAnualFijaGps,
      installments: data.pagoPlazos,
      currentAmount: data.importeActual,
      quoteId: data.numeroIdentificado,
      fixedAmountCoverage: {
        totalDamage: data.coberturaMontoFijo?.danoTotal,
        workshopCare: data.coberturaMontoFijo?.atencionTalleres,
        totalTheft: data.coberturaMontoFijo?.roboTotal,
        brokenWindows: data.coberturaMontoFijo?.roturaLunas,
        theftParts: data.coberturaMontoFijo?.roboPartes,
        civilLiability: data.coberturaMontoFijo?.responsabilidadCivil,
      },
    };
  }
}
